import { IErrorResponse } from '@common/api.models';
import { apiErrorHandle } from '@core/error.services';
import { IFileResponse } from '@file/projectFile/projectFile.models';
import { downloadFile } from '@file/projectFile/projectFile.services';
import { Button, CircularProgressBar, ComponentType, Grid, IconName } from '@gcl/react-core';
import { AxiosError } from 'axios';
import * as React from 'react';
import { useMutation } from '@tanstack/react-query';
import { IHandledError } from '@core/error.models';
import { IDownloadFileBaseProps } from './file.models';

export interface IDownloadButtonProps<T extends IDownloadFileBaseProps> {
  text?: string | undefined;
  type?: ComponentType;
  prefixIconName?: IconName;
  suffixIconName?: IconName;
  onError?: (errors: IHandledError) => void;
  buttonRender?: (exportFile: (props?: Partial<T>) => void) => React.ReactElement;
  shouldDownloadOnSuccess?: boolean;
  defaultExportProps: T;
  exportFunction: (props: T) => Promise<IFileResponse>;
  className?: string;
  disabled?: boolean;
}

export const DownloadButton: <T extends IDownloadFileBaseProps>(
  props: IDownloadButtonProps<T>,
) => React.ReactElement = ({
  text = 'Export',
  type = ComponentType.Secondary,
  prefixIconName = 'ArrowBarDown',
  suffixIconName = undefined,
  onError,
  buttonRender,
  shouldDownloadOnSuccess = true,
  defaultExportProps,
  exportFunction,
  className = '',
  disabled = false,
}) => {
  const [progress, setProgress] = React.useState(0);
  const useMutationResult = useMutation<IFileResponse, any, typeof defaultExportProps, any>(exportFunction, {
    onError: (error: AxiosError<IErrorResponse>) => {
      setProgress(0);
      const errors = apiErrorHandle(error, 'Failed to download file');
      if (onError && typeof onError === 'function') onError(errors);
    },
    onSuccess: (data, variables, context) => {
      setProgress(0);
      if (shouldDownloadOnSuccess) downloadFile(data.file, data.filename);
    },
  });
  const { mutate, isLoading } = useMutationResult;
  const onClickDownloadFile = (customProps?: Partial<typeof defaultExportProps>) => {
    mutate({
      ...defaultExportProps,
      ...(customProps || {}),
      onDownloadProgressUpdate: (percentCompleted: number) => {
        setProgress(percentCompleted);
      },
    });
  };

  return (
    <Grid width="auto" container alignItems="center" flexWrap="nowrap" className={className ?? ''}>
      <Grid item>
        {buttonRender ? (
          buttonRender(onClickDownloadFile)
        ) : (
          <Button
            type={type}
            prefixIconName={prefixIconName}
            suffixIconName={suffixIconName}
            onClick={() => onClickDownloadFile()}
            disabled={isLoading || disabled}
            key="gcl-gems-export-button"
          >
            {text}
          </Button>
        )}
      </Grid>

      <Grid
        item
        className="gcl-pl-xs"
        maxWidth="30px"
        minWidth="30px"
        sx={[isLoading && { visibility: 'visible' }, !isLoading && { display: 'none' }]}
      >
        <CircularProgressBar value={progress} />
      </Grid>
    </Grid>
  );
};
